import React, { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button } from "../../components/common/Primitives";
import api, { formatApiError, API_BASE } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { ShieldCheck, ExternalLink, RefreshCw, CheckCircle2, XCircle, Sparkles, FileText } from "lucide-react";

const STATUS_TONES = {
  requested: "warn",
  payment_pending: "gold",
  active: "success",
  expired: "muted",
  revoked: "danger",
};

const PATH_LABELS = {
  one_off: "One-off",
  subscription: "Subscription",
  tier_bundled: "Institutional tier",
  admin_grant: "Admin grant",
};

export default function AdminDeepDD() {
  const [ents, setEnts] = useState([]);
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, r] = await Promise.all([
        api.get(`/admin/deep-dd/entitlements${filter === "all" ? "" : `?status=${filter}`}`),
        api.get("/admin/deep-dd"),
      ]);
      setEnts(e.data?.items || []);
      setReports(r.data?.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/deep-dd/entitlements/${id}/approve`);
      toast.success("Approved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this entitlement?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/deep-dd/entitlements/${id}/revoke`);
      toast.success("Revoked");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const generateForStartup = async (startupId) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/deep-dd/${startupId}/generate`, {});
      toast.success(`Generated report ${r.data?.report?.report_id || ""}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">PREMIUM PRODUCT</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-1">Deep Due Diligence</h1>
            <div className="text-sm text-ink-soft mt-1">
              Corrit-style deep DD reports — pay-per-report, subscription, tier-bundled or admin-granted access.
            </div>
          </div>
          <div className="text-xs text-ink-muted">
            One-off price: <span className="font-mono text-ink">₹9,999</span> · 30-day window
          </div>
        </div>

        {/* Entitlements queue */}
        <Card testId="deep-dd-entitlements-card">
          <div className="flex items-center justify-between mb-3">
            <div className="overline flex items-center gap-2"><ShieldCheck size={14} /> Access requests & entitlements</div>
            <div className="flex items-center gap-2">
              <select value={filter} onChange={(e) => setFilter(e.target.value)}
                className="text-xs bg-white border border-navy/15 rounded px-2 py-1"
                data-testid="deep-dd-filter">
                <option value="all">All</option>
                <option value="requested">Requested</option>
                <option value="payment_pending">Payment pending</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </select>
              <button onClick={load} className="text-xs px-2 py-1 rounded border border-navy/15" data-testid="deep-dd-refresh">
                <RefreshCw size={12} className="inline" />
              </button>
            </div>
          </div>
          {loading ? (
            <div className="text-sm text-ink-muted italic py-4">Loading…</div>
          ) : ents.length === 0 ? (
            <div className="text-sm text-ink-muted italic py-4" data-testid="deep-dd-empty">
              No entitlements match the current filter.
            </div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {ents.map((e) => (
                <div key={e.entitlement_id} className="py-3 flex items-center justify-between gap-3" data-testid={`ent-${e.entitlement_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Pill tone={STATUS_TONES[e.status] || "muted"}>{e.status.replace("_", " ")}</Pill>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{PATH_LABELS[e.path] || e.path}</span>
                      {e.investor_type && <Pill tone="muted">{e.investor_type}</Pill>}
                    </div>
                    <div className="text-sm">
                      <span className="font-heading text-ink">
                        {e.self_requested
                          ? (e.requester_email || "Founder self-request")
                          : (e.investor_name || e.investor_id || "Admin-initiated")}
                      </span>
                      <span className="text-ink-muted"> → </span>
                      <span className="font-heading text-ink">{e.startup_name || e.startup_id}</span>
                      {e.self_requested && (
                        <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                          Founder self-request
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-ink-muted mt-0.5">
                      Requested {e.requested_at ? formatDateTime(e.requested_at) : "—"}
                      {e.granted_at && <> · granted {formatDateTime(e.granted_at)}</>}
                      {e.expires_at && <> · expires {formatDateTime(e.expires_at)}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {e.status === "requested" && (
                      <button onClick={() => approve(e.entitlement_id)} disabled={busy}
                        className="px-2 py-1 rounded border border-emerald-400 text-emerald-800 hover:bg-emerald-50 text-[11px] font-heading inline-flex items-center gap-1"
                        data-testid={`ent-approve-${e.entitlement_id}`}>
                        <CheckCircle2 size={12} /> Approve
                      </button>
                    )}
                    {e.status === "payment_pending" && (
                      <button
                        onClick={() => {
                          if (window.confirm("Approve this Deep DD request WITHOUT waiting for full payment?\n\nUsed for admin override / concierge deals. Every override is logged in the audit trail.")) {
                            approve(e.entitlement_id);
                          }
                        }}
                        disabled={busy}
                        className="px-2 py-1 rounded border border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 text-[11px] font-heading inline-flex items-center gap-1"
                        data-testid={`ent-override-approve-${e.entitlement_id}`}
                        title="Override payment: approve this Deep DD request even though full payment hasn't landed"
                      >
                        <CheckCircle2 size={12} /> Approve · Override payment
                      </button>
                    )}
                    {e.status === "active" && (
                      <button onClick={() => generateForStartup(e.startup_id)} disabled={busy}
                        className="px-2 py-1 rounded border border-emerald-400 text-emerald-800 hover:bg-emerald-50 text-[11px] font-heading inline-flex items-center gap-1"
                        data-testid={`ent-generate-${e.entitlement_id}`}>
                        <Sparkles size={12} /> Generate
                      </button>
                    )}
                    {(e.status === "active" || e.status === "requested" || e.status === "payment_pending") && (
                      <button onClick={() => revoke(e.entitlement_id)} disabled={busy}
                        className="px-2 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 text-[11px] font-heading inline-flex items-center gap-1"
                        data-testid={`ent-revoke-${e.entitlement_id}`}>
                        <XCircle size={12} /> Revoke
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Reports index */}
        <div className="mt-6">
          <Card testId="deep-dd-reports-card">
            <div className="overline mb-3 flex items-center gap-2"><FileText size={14} /> Generated reports</div>
            {reports.length === 0 ? (
              <div className="text-sm text-ink-muted italic">No reports generated yet.</div>
            ) : (
              <div className="divide-y divide-navy/[0.06]">
                {reports.map((r) => (
                  <div key={r.report_id} className="py-3 flex items-center justify-between gap-3" data-testid={`report-${r.report_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-ink">{r.startup_name || r.startup_id}</div>
                      <div className="text-[11px] font-mono text-ink-muted">
                        {r.report_id} · {formatDateTime(r.generated_at)} · {r.generated_by}
                      </div>
                    </div>
                    <a href={`${API_BASE}/admin/deep-dd/${r.report_id}/pdf`}
                      target="_blank" rel="noopener noreferrer"
                      className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1"
                      data-testid={`report-pdf-${r.report_id}`}>
                      <ExternalLink size={12} /> Open PDF
                    </a>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardShell>
  );
}
